import { observable, action, makeObservable } from 'mobx'
import * as R from 'ramda'
import PowerpointBuilderStore from '../../Presentations/PresentationsStore'

const stages = Object.freeze({
    INIT_OPTIONS: 'initOptions',
    PICK_AGGREGATED_FORMATS: 'aggregated',
    PICK_RAW_FORMATS: 'raw',
    PPT: 'ppt', // not implemented
    PDF: 'pdf', // not implemented
    SELECT_DOWNLOAD_OPTIONS: 'downloadOptions',
    DOWNLOADING: 'downloading',
    NOT_AVAILABLE: 'n/a',
})

const formatsWithOptions = ['csv']
class Store {
    @observable stage = stages.INIT_OPTIONS

    @observable loading = false

    socketDownloads = new Set(['csv', '3s', 'cm'])

    pageType

    downloadType

    downloadError

    downloadUrl = {
        primary: '',
        secondary: '',
    }

    downloadOptions = {}

    downloadAttribute = ''

    constructor(pageType, store, reportStore = null) {
        makeObservable(this)
        this.appStore = store
        this.reportStore = reportStore
        this.pageType = pageType
    }

    @action
    reset = (pageType) => {
        this.loading = false
        this.downloadUrl = {}
        this.downloadOptions = {}
        Reflect.deleteProperty(this, 'downloadType')
        Reflect.deleteProperty(this, 'downloadError')
        Reflect.deleteProperty(this, 'downloadAttribute')
        if (pageType === 'report') Reflect.deleteProperty(this.reportStore, 'questionKey')
        this.stage = stages.INIT_OPTIONS
    }

    setStage = (stage) => {
        this.stage = stage
    }

    setDownloadType = (downloadType) => {
        this.downloadType = downloadType
        const verbatimReport = process.env.USE_VERBATIM_PDF && downloadType === 'pdf' && this.pageType === 'report'
        this.stage = (formatsWithOptions.includes(downloadType) || verbatimReport)
            ? stages.SELECT_DOWNLOAD_OPTIONS
            : stages.DOWNLOADING
    }

    getDownloadType = () => {
        const { downloadType } = this
        switch (downloadType) {
            case 'xlsx':
                return 'excel'
            case '3s':
                return 'triple s'
            default:
                return downloadType
        }
    }

    setDownloadUrl = (primary, secondary) => {
        this.downloadUrl = { primary, secondary }
        this.loading = false
    }

    setError = (error) => {
        this.downloadError = error
        this.loading = false
    }

    isSocketDownload = (downloadType) => (
        this.socketDownloads.has(downloadType)
    )

    getSocketQuery = (pageType, downloadType) => {
        const { appStore } = this
        let query = ''
        if (pageType === 'survey') {
            query = '&target=export&method=questionLangs'
        } else if (pageType === 'report') {
            switch (downloadType) {
                case 'csv':
                    query = '&target=export&method=csvExport'
                    break
                case '3s':
                    query = '&target=export&method=tripleS'
                    break
                default:
                    return ''
            }
        }
        query += `&token=${appStore.token}`
        return query
    }

    getSocketOptions = (pageType, appStore, includeQuestions) => {
        let options
        if (pageType === 'survey') {
            options = {
                kind: 'questionLangExport',
                surveyId: appStore.survey.id,
                delimiter: this.downloadOptions.delimiter ?? 'comma',
                userId: appStore.tokenData.userId,
                sessionId: appStore.userData.sessionId,
            }
        } else if (pageType === 'report') {
            options = this.reportStore.getDownloadRequest(this.downloadType, this.downloadOptions, includeQuestions)
        }
        return options
    }

    download = (includeQuestions) => {
        const { downloadType } = this
        this.loading = true

        if (this.isSocketDownload(downloadType)) {
            this.socketDownload(downloadType, includeQuestions)
            return
        }
        if (this.pageType === 'report') {
            if (downloadType === 'xlsx') {
                this.reportXlsDownload(includeQuestions)
            } else if (downloadType === 'pdf') {
                this.reportPdfDownload(includeQuestions)
            } else if (downloadType === 'ppt') {
                this.reportPptDownload(includeQuestions)
            }
        } else if (this.pageType === 'survey' && downloadType === 'pdf') {
            this.surveyPdfDownload()
        }
    }

    // Web socket downloads
    socketDownload = (downloadType, includeQuestions) => {
        const { appStore } = this
        const wsUrl = appStore.http.gatewayUrl.replace(/https:\/\//, 'wss://').replace(/http:\/\//, 'ws://')
        const socket = io(wsUrl, {
            query: this.getSocketQuery(this.pageType, downloadType),
        })
        socket.on('connection', (data) => {
            console.log(`connection ${data}`)
            socket.emit('data', JSON.stringify(this.getSocketOptions(this.pageType, appStore, includeQuestions)))
        })
        socket.on('response', (data) => {
            console.log(`response ${data}`)
            socket.disconnect()
            const [primary, secondary] = data.split(',')
            this.setDownloadUrl(primary, secondary)
        })

        socket.onerror = function onerror(error) {
            console.log(`onerror ${error}`)
            this.loading = false
            this.setError(error)
            socket.disconnect()
        }
    }

    // Other downloads
    reportXlsDownload = async (includeQuestions) => {
        const { appStore, reportStore } = this
        const { questionKey } = reportStore

        const data = reportStore.generateExcelData(questionKey, includeQuestions)

        if (data === false) {
            this.setError('Error downloading excel')
            return
        }

        const link = await appStore.http.post('/export/excel', JSON.stringify({
            kind: 'Report',
            entrySheet: R.isNil(questionKey),
            data,
        }))

        this.setDownloadUrl(link)
    }

    reportPdfDownload = async (includeQuestions) => {
        const { appStore, reportStore } = this
        const pdfData = reportStore.generatePdfData(this.downloadOptions.openEnded ?? false, includeQuestions)
        const { surveyId } = pdfData

        try {
            const url = await appStore.http.post(
                `/puppeteer/pdf/v2/${surveyId}`,
                JSON.stringify(pdfData),
            )

            this.setDownloadUrl(url)
        } catch (e) {
            console.error(e)
            this.setError('Error downloading report PDF')
        }
    }

    // Builds the same ppPresentation the presentation builder downloads - one 1x1 slide
    // per included question, every chart on its standard defaults - and posts it to the
    // same endpoint, so a report export and a hand-built deck render identically.
    reportPptDownload = async (includeQuestions) => {
        const { appStore, reportStore } = this
        const { benchmarks2, benchmarkPeriod, questionKey } = reportStore

        const pptStore = new PowerpointBuilderStore(appStore)
        try {
            await pptStore.initForExport()

            // Carry the report's benchmark period, benchmarks and year comparisons over
            // before building, since each question reads them as it takes its defaults.
            const period = pptStore.benchmarkPeriods
                .find(({ period: p }) => p.duration === benchmarkPeriod?.duration)
            if (period) pptStore.updateGlobalBenchmark({ value: period.value })

            Array.from(benchmarks2 ?? [])
                .filter(([, enabled]) => enabled)
                .forEach(([key]) => {
                    pptStore.handleToggleGlobalBenchmark({ name: key, value: true })
                })

            // The report tracks comparisons by survey id, the builder by year.
            const comparisonYears = new Set(R.or(reportStore.getReportComparisons(), [])
                .map((comparisonId) => pptStore.surveys.find((s) => (
                    s.surveyId?.toUpperCase() === String(comparisonId).toUpperCase()
                ))?.year)
                .filter((year) => year != null))

            comparisonYears.forEach((year) => {
                pptStore.handleToggleGlobalYear({ name: String(year), value: true })
            })

            const presentation = pptStore.buildPresentationForQuestions({
                questionIds: includeQuestions,
                pageQuestionId: questionKey,
                title: appStore.survey?.title ?? '',
                filter: {
                    state: reportStore.getReportStates(),
                    surveyDataIds: [],
                    question: { ruleType: 'ruleGroup', rules: [] },
                    campaign: { ruleType: 'ruleGroup', rules: [] },
                    responseDate: { ruleType: 'ruleGroup', rules: [] },
                },
            })

            if (presentation.section[0].slide.length === 0) {
                this.setError('No questions in this report can be exported to PowerPoint')
                return
            }

            this.setDownloadUrl(await pptStore.postPresentation(presentation))
        } catch (e) {
            console.error(e)
            this.setError('Error downloading report PowerPoint')
        } finally {
            pptStore.dispose()
        }
    }

    surveyPdfDownload = () => {
        const { appStore } = this
        const { survey, surveyLocale } = appStore
        this.loading = true

        appStore.http.get(`/puppeteer/preview/${survey.id}/${surveyLocale}`)
            .then((blob) => {
                const a = document.createElement('a')
                a.href = window.URL.createObjectURL(blob)
                a.setAttribute('download', `${survey.id}.pdf`)
                a.click()
                this.reset(this.pageType)
                appStore.downloadModalOpen = false
                appStore.addNotification(null, 'Download finished', 'success')
            })
            .catch((e) => {
                console.error(e)
                this.hideDownloadModal()
                appStore.addNotification(null, 'Download failed', 'error')
            })
    }
}

export { Store, stages }
